import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileDown, Upload, Loader2, X, AlertCircle, FileSpreadsheet, Trash2 } from 'lucide-react';
import Papa from 'papaparse';
import { supabase } from '@/lib/supabaseClient';
import { useOutletContext } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useVisibleCategories } from '@/hooks/useVisibleCategories';

const parseDateToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    // If it's already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    // Handle DD/MM/YYYY or D/M/YYYY
    if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            let day = parts[0];
            let month = parts[1];
            let year = parts[2];
            // Support YYYY/MM/DD
            if (day.length === 4) {
                year = parts[0];
                month = parts[1];
                day = parts[2];
            } else if (year.length === 2) {
                year = '20' + year; // rudimentary 2-digit year handling
            }
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }

    // Fallback to JS standard Date parsing
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
    }
    return '';
};

const CsvImporter = ({ onClose, onSuccess }) => {
    const { currentOrg, userData } = useOutletContext();
    const { flatCategories = [], loading: categoriesLoading } = useVisibleCategories(currentOrg);
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [data, setData] = useState([]);
    const [saving, setSaving] = useState(false);

    // Download the template
    const handleDownloadTemplate = async () => {
        try {
            const templateRows = flatCategories.map(cat => {
                const today = new Date();
                const startDate = today.toISOString().split('T')[0];

                // Set End Date for period-based emissions (Electricity, Gas, Water)
                const isPeriodBased = cat.value.includes('electricity') ||
                    cat.value === 'natural_gas' ||
                    cat.value === 'water' ||
                    cat.value.includes('waste');

                let endDate = '';
                if (isPeriodBased) {
                    const end = new Date(today);
                    end.setMonth(end.getMonth() + 1);
                    endDate = end.toISOString().split('T')[0];
                }

                return {
                    'Category': cat.value,
                    'Name/Source': cat.label,
                    'Start Date (YYYY-MM-DD)': startDate,
                    'End Date (YYYY-MM-DD)': endDate,
                    'Quantity': '10.0', // Example quantity
                    'Unit': cat.unit,
                    'Notes': `Example for ${cat.label}`
                };
            });

            // If no categories yet (loading or empty), provide a fallback header
            const csvData = templateRows.length > 0
                ? Papa.unparse(templateRows)
                : 'Category,Name/Source,Start Date (YYYY-MM-DD),End Date (YYYY-MM-DD),Quantity,Unit,Notes';

            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const { saveAs } = await import('file-saver');
            saveAs(blob, 'emission_entries_template.csv');
            toast.success('Generated customized template based on your active factors');
        } catch (error) {
            console.error('Error generating template:', error);
            toast.error('Failed to generate template file');
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            toast.error('Please upload a valid CSV file');
            return;
        }

        setFile(selectedFile);
        parseCSV(selectedFile);
    };

    const parseCSV = (fileToParse) => {
        setParsing(true);
        Papa.parse(fileToParse, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const parsedData = results.data.map((row, index) => {
                    // Attempt to map headers flexibly in case users messed up case
                    const getVal = (possibleKeys) => {
                        const key = Object.keys(row).find(k => possibleKeys.some(pk => k.toLowerCase().includes(pk)));
                        return key ? row[key] : '';
                    };

                    return {
                        id: index.toString(), // Temp ID for React keys
                        category: getVal(['category']) || '',
                        name: getVal(['name', 'source']) || '',
                        start_date: parseDateToYYYYMMDD(getVal(['start dt', 'start date']) || ''),
                        end_date: parseDateToYYYYMMDD(getVal(['end dt', 'end date']) || ''),
                        quantity: getVal(['quantity', 'amount', 'qty']) || '',
                        unit: getVal(['unit']) || '',
                        notes: getVal(['note']) || '',
                        isValid: false, // Calculated next
                        errors: {} // To highlight specific cells
                    };
                });

                // Basic validation
                const validatedData = parsedData.map(row => validateRow(row));
                setData(validatedData);
                setParsing(false);
            },
            error: (error) => {
                console.error('Error parsing CSV:', error);
                toast.error('Failed to parse CSV file');
                setParsing(false);
            }
        });
    };

    const validateRow = (row) => {
        const errors = {};

        const cat = row.category?.toLowerCase();
        const validCategories = flatCategories.map(c => c.value);
        if (!cat || !validCategories.includes(cat)) {
            errors.category = true;
        }

        if (!row.start_date || isNaN(Date.parse(row.start_date))) {
            errors.start_date = true;
        }

        if (row.end_date && isNaN(Date.parse(row.end_date))) {
            errors.end_date = true;
        }

        if (!row.quantity || isNaN(parseFloat(row.quantity))) {
            errors.quantity = true;
        }

        if (!row.name || row.name.trim().length === 0) {
            errors.name = true;
        }

        return {
            ...row,
            category: cat || '',
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleFieldChange = (id, field, value) => {
        setData(prev => {
            const updated = prev.map(row => {
                if (row.id === id) {
                    let newRow = { ...row, [field]: value };

                    // Auto-map name and unit if category is changed
                    if (field === 'category') {
                        const matchedCat = flatCategories.find(c => c.value === value);
                        if (matchedCat) {
                            newRow.name = matchedCat.label;
                            newRow.unit = matchedCat.unit;
                        }
                    }

                    return validateRow(newRow);
                }
                return row;
            });
            return updated;
        });
    };

    const handleDeleteRow = (id) => {
        setData(prev => prev.filter(row => row.id !== id));
    };

    const handleSave = async () => {
        const invalidRows = data.filter(r => !r.isValid);
        if (invalidRows.length > 0) {
            toast.error(`Please fix ${invalidRows.length} invalid rows before saving`);
            return;
        }

        if (data.length === 0) {
            toast.error('No data to save');
            return;
        }

        setSaving(true);

        try {
            // Map to DB structure
            const rowsToInsert = data.map(row => ({
                organisation_id: currentOrg.id,
                user_id: userData.id,
                category: row.category,
                name: row.name,
                type: 'emission',
                start_date: row.start_date,
                end_date: row.end_date || row.start_date, // fallback to point-in-time
                quantity: parseFloat(row.quantity),
                unit: row.unit,
                notes: row.notes,
                is_date_range: !!row.end_date && row.end_date !== row.start_date
            }));

            const { error } = await supabase
                .from('emissions_data')
                .insert(rowsToInsert);

            if (error) throw error;

            toast.success(`Successfully imported ${rowsToInsert.length} entries`);
            onSuccess?.();
            onClose();

        } catch (err) {
            console.error('Error saving imported data:', err);
            toast.error('Failed to save data. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-xl">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 flex items-center justify-between shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#74A57F] to-[#5C8A66] rounded-lg flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Import Emissions Data
                            </h2>
                            <p className="text-sm text-slate-500">
                                Upload a CSV file to bulk import emission entries.
                            </p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {/* Content Body */}
                <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
                    {!file ? (
                        <div className="max-w-xl mx-auto space-y-6">
                            <div className="bg-white p-6 rounded-xl border border-slate-200 text-center">
                                <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-slate-800 mb-2">Upload your CSV</h3>
                                <p className="text-sm text-slate-500 mb-6">
                                    Select a prepared CSV file with your emissions data.
                                </p>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                                >
                                    Browse Files
                                </Button>
                            </div>

                            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 flex items-start space-x-4">
                                <AlertCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-blue-900 mb-1">Need the CSV Template?</h4>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Download our standard template structure to ensure your data imports correctly on the first try.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-200 text-blue-700 hover:bg-blue-100"
                                    >
                                        <FileDown className="w-4 h-4 mr-2" />
                                        Download Template
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200">
                                <span className="font-medium text-[#003B5C]">{file.name}</span>
                                <span className="text-sm text-slate-500">{data.length} rows detected</span>
                            </div>

                            {parsing ? (
                                <div className="py-12 flex justify-center items-center flex-col text-slate-400">
                                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                                    <p>Parsing CSV file...</p>
                                </div>
                            ) : (
                                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-[#003B5C] text-white">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-medium">Status</th>
                                                    <th className="px-4 py-3 text-left font-medium">Category</th>
                                                    <th className="px-4 py-3 text-left font-medium">Name/Source</th>
                                                    <th className="px-4 py-3 text-left font-medium">Start Date</th>
                                                    <th className="px-4 py-3 text-left font-medium">End Date</th>
                                                    <th className="px-4 py-3 text-left font-medium">Qty</th>
                                                    <th className="px-4 py-3 text-left font-medium">Unit</th>
                                                    <th className="px-4 py-3 text-left font-medium">Notes</th>
                                                    <th className="px-4 py-3 text-left font-medium text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {data.map((row) => (
                                                    <tr key={row.id} className={`hover:bg-slate-50 ${!row.isValid ? 'bg-red-50/30' : ''}`}>
                                                        <td className="px-4 py-2 text-center">
                                                            {row.isValid ? (
                                                                <div className="w-3 h-3 rounded-full bg-green-500 m-auto" title="Valid"></div>
                                                            ) : (
                                                                <div className="w-3 h-3 rounded-full bg-red-500 m-auto" title="Invalid data - please check dates, quantities and category"></div>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Select
                                                                value={row.category}
                                                                onValueChange={(val) => handleFieldChange(row.id, 'category', val)}
                                                            >
                                                                <SelectTrigger className={`h-8 w-[160px] text-xs ${row.errors.category ? 'border-red-500 bg-red-50' : ''}`}>
                                                                    <SelectValue placeholder="Category" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {categoriesLoading ? (
                                                                        <div className="p-2 text-xs text-slate-500">Loading...</div>
                                                                    ) : (
                                                                        flatCategories.map((cat) => (
                                                                            <SelectItem key={cat.value} value={cat.value} className="text-xs">
                                                                                {cat.formattedLabel || cat.label}
                                                                            </SelectItem>
                                                                        ))
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                value={row.name}
                                                                onChange={(e) => handleFieldChange(row.id, 'name', e.target.value)}
                                                                className={`h-8 w-[140px] text-xs ${row.errors.name ? 'border-red-500 bg-red-50' : ''}`}
                                                                placeholder="Location/Source"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="date"
                                                                value={row.start_date || ''}
                                                                onChange={(e) => handleFieldChange(row.id, 'start_date', e.target.value)}
                                                                className={`h-8 w-[120px] text-xs ${row.errors.start_date ? 'border-red-500 bg-red-50' : ''}`}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="date"
                                                                value={row.end_date || ''}
                                                                onChange={(e) => handleFieldChange(row.id, 'end_date', e.target.value)}
                                                                className={`h-8 w-[120px] text-xs ${row.errors.end_date ? 'border-red-500 bg-red-50' : ''}`}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="number"
                                                                value={row.quantity}
                                                                onChange={(e) => handleFieldChange(row.id, 'quantity', e.target.value)}
                                                                className={`h-8 w-24 text-xs ${row.errors.quantity ? 'border-red-500 bg-red-50' : ''}`}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                value={row.unit}
                                                                onChange={(e) => handleFieldChange(row.id, 'unit', e.target.value)}
                                                                className="h-8 w-16 text-xs"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                value={row.notes}
                                                                onChange={(e) => handleFieldChange(row.id, 'notes', e.target.value)}
                                                                className="h-8 text-xs"
                                                                placeholder="..."
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDeleteRow(row.id)}
                                                                className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {file && !parsing && (
                    <div className="p-6 border-t border-slate-200 bg-white shrink-0 flex items-center justify-between">
                        <div className="text-sm">
                            <span className="text-slate-500">Rows ready to import:</span>
                            <span className="ml-2 font-medium text-green-600">
                                {data.filter(r => r.isValid).length} valid
                            </span>
                            <span className="mx-2 text-slate-300">|</span>
                            <span className="font-medium text-red-600">
                                {data.filter(r => !r.isValid).length} invalid
                            </span>
                        </div>
                        <div className="flex space-x-3">
                            <Button
                                variant="outline"
                                onClick={() => { setFile(null); setData([]); }}
                                disabled={saving}
                            >
                                Start Over
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving || data.filter(r => !r.isValid).length > 0 || data.length === 0}
                                className="bg-[#74A57F] hover:bg-[#5C8A66] text-white"
                            >
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                Confirm & Import {data.length} Entries
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CsvImporter;
